import axios from "axios";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import CommonFilterModal from "../../../components/Common/CommonFilterModal";
import NavBarTitles from "../../../components/Common/TabBar";
import {
  Fees,
  doctorProfileStatus,
  mode,
  status,
} from "../../../components/Constants";
import Deletepopup from "../../../components/Modal/Deletepopup";
import ExcelDownload from "../../../components/Utils/excelDownload";
import ExcelDownloadModal from "../../../Pages/Masters/Common/excelDownloadAccess";

// Updated custom hook paths
import Actions from "../../../components/Common/Actions";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import SelectComponent from "../../../components/Common/Select";
import ListPageLayout from "../../../components/Layout";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";
import { PartneredDoctornavBarData } from "../DoctorConst";

const emptyFilterValues = {
  "Primary Specialisation": [],
  Status: [],
  Gender: [],
  Mode: [],
  Fee: [],
  Language: [],
};

const PartneredDoctorForInClinic = () => {
  const navigate = useNavigate();
  const addDoctors = {
    id: "addnewpost",
    onClick: () => navigate("/partnered-doctor-form-create"),
    style: { width: "240px" },
    label: "Add Doctors",
  };
  const [loader, setLoader] = useState(true);

  // Sorting
  const [sort, setSort] = useState("newest");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }))
  );

  // Filter states
  const [primarySpl, setPrimarySpl] = useState([]);
  const [gender, setGender] = useState([]);
  const [language, setLanguage] = useState([]);

  const [selectedValues, setSelectedValues] = useState(emptyFilterValues);
  const [tempSelectedValues, setTempSelectedValues] =
    useState(emptyFilterValues);

  const [selectedCategory, setSelectedCategory] = useState(
    "Primary Specialisation"
  );
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}partnered-doctor/list`,
      initialParams: {
        sort,
        searchText: search,
        selectedValues,
        profile_mode: "InClinic",
        strictProfileMode: true,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "partnered-doctor",
    fetchData,
    setShowDelete
  );

  const fetchAndMap = async (url, props = "", mapFn) => {
    try {
      const { data } = await axios.post(
        ` ${process.env.REACT_APP_API_URL}${url}`,
        props
      );
      return data?.rows?.map(mapFn) || [];
    } catch (error) {
      console.error(`Error fetching ${url}:`, error);
      return [];
    }
  };

  // const getsrcValue = (value) => {
  //   if (typeof value === "string" && value.length)
  //     return process.env.REACT_APP_API_URL + "profile-images/" + value;
  // };

  const getsrcValue = (value) => {
    if (!value) return "/assets/doctor-placeholder.png"; // fallback placeholder

    // ✅ If it's already a full S3 URL
    if (
      typeof value === "string" &&
      (value.startsWith("http://") || value.startsWith("https://"))
    ) {
      return value;
    }

    // ✅ Otherwise assume it’s a legacy local path
    return `${process.env.REACT_APP_API_URL}profile-images/${value}`;
  };

  useEffect(() => {
    const fetchData = async () => {
      const [primarySpls, genders, languages] = await Promise.all([
        fetchAndMap(
          "doctor-specialisation/list",
          { noLimit: true },
          (data) => data.online_doctor_specialization
        ),

        fetchAndMap("gender/list", { noLimit: true }, (gender) => gender?.type),
        fetchAndMap("languages/list", { noLimit: true }, (data) => data?.type),
      ]);
      setPrimarySpl(primarySpls);
      setGender(genders);
      setLanguage(languages);
    };

    fetchData();
  }, []);

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);

  useEffect(() => {
    setLoader(true);
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search, selectedValues]);

  const filters = {
    "Primary Specialisation": primarySpl,
    Status: status,
    Gender: gender,
    City: [],
    Mode: mode,
    Fee: Fees, // import
    Language: language,
  };

  const handleOptionChange = (e, id) => {
    const selectedValue = e;
    const fields = {};
    setLoader(true);
    fields.action = selectedValue;
    axios
      .post(
        `${process.env.REACT_APP_API_URL}partnered-doctor/update/${id}`,
        fields
      )
      .then(() => {
        fetchData();
        // getInstaDocQrCodeList()
      });

    if (selectedValue === "Delete") {
      setShowDelete({ id: id, status: true });
    }
  };

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    // {
    //   title: "Image",
    //   fixed: "left",
    //   render: (text, record) => (
    //     <img
    //       src={getsrcValue(record.profileImage)}
    //       className="doctorProfileImage"
    //       alt="doctor"
    //     />
    //   ),
    // },

    {
      title: "Image",
      fixed: "left",
      render: (text, record) => (
        <img
          src={getsrcValue(record.profileImage)}
          className="doctorProfileImage"
          alt="doctor"
          onError={(e) => (e.target.src = "/assets/doctor-placeholder.png")}
        />
      ),
    },

    {
      title: "Name of Doctor",
      dataIndex: "doctor_name",
      key: "doctor_name",
      fixed: "left",
    },
    {
      title: "Created at",
      render: (text, record) => (
        <span>
          {moment(record.createdAt).format("DD-MM-YYYY")}{" "}
          {moment(record.createdAt).format("LT")}
        </span>
      ),
    },
    {
      title: "Last updated on",
      dataIndex: "updatedAt",
      render: (text) => moment(text).format("DD-MM-YYYY"),
      width: 220,
    },
    {
      title: "Degree",
      render: (text, record) => (
        <span>
          {typeof record?.degree === "object" ? record?.degree?.ug_degree : ""}
          {typeof record?.education === "string" ? record?.education : ""}
          {record.pg_degree
            ? record?.pg_degree?.pg_degree + ", " + record?.pg_education
            : null}
        </span>
      ),
    },
    {
      title: "Specialization (Primary)",
      dataIndex: "doctor_primary_specialization",
      key: "doctor_primary_specialization",
      render: (text) => text?.online_doctor_specialization,
    },
    {
      title: "Fee",
      dataIndex: "online_consultation_fee",
      key: "online_consultation_fee",
    },
    {
      title: "Language",
      render: (text, record) =>
        record?.languages_known?.map((item) => item?.type)?.join(", "),
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (text) => text?.city,
    },
    {
      title: "Mode",
      dataIndex: "profile_mode",
      key: "profile_mode",
    },
    {
      title: "Mobile Number",
      dataIndex: "mobile",
      key: "mobile",
    },
    {
      title: "Status",
      width: 150,
      render: (text, record) => (record.verified ? "Verified" : "Not verified"),
    },
    {
      title: "Profile Status of the Partner",
      key: "Profile Status of the Partner",
      render: (_, record) => (
        // <select
        //   className="bg-white"
        //   onChange={(e) => handleOptionChange(e, record.key)}
        //   style={{ color: "#CB1B5B", minWidth: "140px", borderRadius: "5px", border: "1px solid" }}
        //   value={record.action}
        // >
        //   {doctorProfileStatus.map((action, inx) => {
        //     return (
        //       <option key={inx} value={action} className="text-dark">
        //         {action}
        //       </option>
        //     );
        //   })}
        // </select>
        <SelectComponent
          props={{
            Array: doctorProfileStatus,
            value: getSelecedValue(record),
            style: { color: "#CB1B5B", width: "200px" },
            onChange: async (e) => await handleOptionChange(e, record.key),
          }}
        />
      ),
    },
    {
      title: "Action",
      width: 200,
      render: (text, record) => (
        <Actions
          isEdit={true}
          isDelete={true}
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() => navigate(`/partnered-doctor/edit/${record._id}`)}
        />
      ),
    },
  ];

  const onClickDownload = async () => {
    try {
      const formattedRecords = records.map((record, index) => ({
        "S.No": index + 1,
        "Time of Registration": record.createdAt
          ? new Date(record.createdAt).toLocaleTimeString()
          : "",
        "Date of Registration": record.createdAt
          ? new Date(record.createdAt).toLocaleDateString()
          : "",
        "Name of Doctor": record.doctor_name || "",
        Degree: record.degree?.ug_degree || "",
        "Specialization (Primary)":
          record.doctor_primary_specialization?.online_doctor_specialization ||
          "",
        Fee: record.online_consultation_fee || "",
        Language:
          record.languages_known?.map((item) => item?.type)?.join(", ") || "",
        city: record.city?.city || "",
        Mode: record.profile_mode || "",
        "Mobile Number": record.mobile || "",
        Status: record.verified ? "Verified" : "Not Verified",
      }));

      ExcelDownload(formattedRecords, "", "partnered-doctor.xlsx");
    } catch (err) {
      console.error(
        "Error downloading the file while getting all Doctor registration records",
        err,
        "err.message",
        err?.message
      );
      alert(err?.message || "An error occurred while downloading the file.");
    }
  };

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "/partnered-doctor-online",
      },
      {
        text: "Add Doctors",
        link: "",
      },
    ],
    []
  );
  const getSelecedValue = (data) => {
    return data?.action ? data?.action : "Select";
  };

  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className="healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      loader={loader}
      addButton={<FilledButton props={addDoctors} />}
      titles={<NavBarTitles titles={PartneredDoctornavBarData} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          sortName={sortName}
          search={search}
          setFilterShow={() => setIsFilterModalVisible(true)}
          // setSortShow={setSortShow}
          onChange={debouncedSearch}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          downloadExcel={true}
          onClickDownload={() => setIsModalOpen(true)}
          handleSortClick={handleSortClick}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}

      <CommonFilterModal
        visible={isFilterModalVisible}
        onClose={() => setIsFilterModalVisible(false)}
        onApply={() => {
          setSelectedValues(tempSelectedValues);
          setIsFilterModalVisible(false);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          // fetchData();
        }}
        onReset={() => {
          setTempSelectedValues(emptyFilterValues);
          setSelectedValues(emptyFilterValues);
          setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 }));
          fetchData();
        }}
        filters={filters}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        selectedValues={tempSelectedValues}
        setSelectedValues={setTempSelectedValues}
      />
      <ExcelDownloadModal
        onClickDownload={onClickDownload}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        searchOnlyCategories={["City / Town"]}
      />
    </ListPageLayout>
  );
};

export default PartneredDoctorForInClinic;
